import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/ui/Card/Card';
import Input from '../../components/ui/Input/Input';
import Button from '../../components/ui/Button/Button';
import { register, login, verifyOTP, getUserAccess, getUserIncubationAccess } from '../../controllers/users.js';
import { useToast } from '../../contexts/ToastContext';
import { useUser } from '../../contexts/UserContext';
import './LandingScreen.css';

const Landing = () => {
  const navigate = useNavigate();
  const { showError, showWarning } = useToast();
  const { setGlobalsFromApi, setIncubationAccessFromApi, setCurrentUser } = useUser();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const currentCodeRef = useRef('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSignIn = async () => {
    if (!formData.email) {
      showWarning('Please enter your email');
      return;
    }

    setIsLoading(true);
    try {
      let response = await login({ email: formData.email });
      console.log(response.data);
      if (response.data.success) {
        setIsVerifying(true);
      } else {
        showError(response.data.error);
      }
    } catch (error) {
      console.error('Sign in failed:', error);
      showError('Sign in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isVerifying) {
      // reset any previous code state and focus first input
      setCodeDigits(['', '', '', '', '', '']);
      setVerificationCode('');
      if (inputRefs.current && inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }
  }, [isVerifying]);

  const handleVerifyCode = async () => {
    const currentCode = currentCodeRef.current;
    console.log('Verifying code:', currentCode, 'Length:', currentCode.length);
    if (!currentCode || currentCode.length !== 6) {
      showWarning('Please enter the 6-digit verification code');
      return;
    }

    setIsLoading(true);
    try {
      let response = await verifyOTP({ token: currentCode, email: formData.email });
      console.log(response.data);
      if (response.data.success) {
        // Store current user info
        const userInfo = {
          email: formData.email,
          user_id: response.data.user_id, // Assuming the response includes user_id
          // Add any other user info from the response if available
        };
        setCurrentUser(userInfo);

        if (response.data.user_id) {
          try {
            const [accessResp, incubationAccessResp] = await Promise.all([
              getUserAccess({ user_profile_id: response.data.user_id }).catch(() => null),
              getUserIncubationAccess({ user_profile_id: response.data.user_id }).catch(() => null)
            ]);
            if (accessResp?.data?.success && accessResp.data.userAccess) {
              setGlobalsFromApi(accessResp.data.userAccess);
            }
            if (incubationAccessResp?.data?.success) {
              setIncubationAccessFromApi(incubationAccessResp.data);
            }
          } catch (userAccessError) {
            console.warn('Could not load user access data on login:', userAccessError);
          }
        }

        navigate('/home');
      } else {
        showError(response.data.error);
      }
    } catch (error) {
      console.error('Verification failed:', error);
      showError('Verification failed. Please check your code and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUIClick = () => {
    navigate('/ui');
  };

  return (
    <div className="landing-container">
      <div className="signin-card-container">
        <img src="https://teletailstest.s3.amazonaws.com/chat/1754688328693.png" alt="Teletails Logo" className="signin-icon" />
        <Card style={{ padding: '15px 25px 25px 25px' }}>
          <div className="signin-header">
            <h1 className="signin-title">{isVerifying ? 'Enter Verification Code' : 'Sign In'}</h1>
          </div>
          
          {!isVerifying ? (
            <>
              <div className="form-group">
                <label className="form-label">Email</label>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSignIn();
                    }
                  }}
                  className="form-input"
                />
              </div>


              <Button
                onClick={handleSignIn}
                className="signin-button"
                variant="primary"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Sign In'}
              </Button>
            </>
          ) : (
            <>
              <div className="form-group">
                <p className="verification-message">
                  We sent you a code, please verify it here
                </p>
                <div
                  className="code-inputs-container"
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text') || '';
                    const digits = pasted.replace(/\D/g, '').slice(0, 6).split('');
                    if (digits.length > 0) {
                      const next = ['','', '', '', '', ''];
                      for (let i = 0; i < 6; i++) {
                        next[i] = digits[i] || '';
                      }
                      setCodeDigits(next);
                      const joined = next.join('');
                      setVerificationCode(joined);
                      currentCodeRef.current = joined;
                      const focusIndex = Math.min(digits.length, 5);
                      const el = inputRefs.current[focusIndex];
                      if (el) el.focus();
                      e.preventDefault();
                      // Auto-verify when a complete 6-digit code is pasted
                      if (joined.length === 6) {
                        setTimeout(() => {
                          handleVerifyCode();
                        }, 100);
                      }
                    }
                  }}
                >
                  {codeDigits.map((digit, idx) => (
                    <input
                      key={idx}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength="1"
                      className="code-input"
                      value={digit}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const val = raw.replace(/\D/g, '');
                        const next = [...codeDigits];
                        next[idx] = val ? val[0] : '';
                        setCodeDigits(next);
                        const joined = next.join('');
                        setVerificationCode(joined);
                        currentCodeRef.current = joined;
                        if (val && idx < 5) {
                          const el = inputRefs.current[idx + 1];
                          if (el) el.focus();
                        }
                        // Auto-verify when all 6 digits are entered
                        if (joined.length === 6) {
                          setTimeout(() => {
                            handleVerifyCode();
                          }, 100);
                        }
                      }}
                      onKeyDown={(e) => {
                        const key = e.key;
                        if (key === 'Backspace') {
                          if (codeDigits[idx]) {
                            const next = [...codeDigits];
                            next[idx] = '';
                            setCodeDigits(next);
                            const joined = next.join('');
                            setVerificationCode(joined);
                            currentCodeRef.current = joined;
                          } else if (idx > 0) {
                            const el = inputRefs.current[idx - 1];
                            if (el) {
                              el.focus();
                            }
                          }
                        } else if (key === 'ArrowLeft' && idx > 0) {
                          const el = inputRefs.current[idx - 1];
                          if (el) el.focus();
                        } else if (key === 'ArrowRight' && idx < 5) {
                          const el = inputRefs.current[idx + 1];
                          if (el) el.focus();
                        } else if (key === 'Enter') {
                          if (verificationCode && verificationCode.length === 6) {
                            handleVerifyCode();
                          }
                        }
                      }}
                      ref={(el) => (inputRefs.current[idx] = el)}
                      disabled={isLoading}
                    />
                  ))}
                </div>
              </div>

              <Button
                onClick={handleVerifyCode}
                className="signin-button"
                variant="primary"
                disabled={isLoading || codeDigits.join('').length !== 6}
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </Button>
            </>
          )}

          <div className="form-footer">
            <span className="support-text">Support</span>
            <a href="mailto:fetchboard@teletails.com" className="support-email">fetchboard@teletails.com</a>
          </div>
        </Card>
        <div className="spacer-container"></div>
      </div>
    </div>
  );
};

export default Landing; 